
import React, { useMemo } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { getStatusStyle } from "../../../utils/statusStyles";

import type { ManagerScheduledOrder,ScheduleOrdersTableProps } from "../interface/ScheduledOrder ";

import {
  formatScheduleDate,
  formatScheduleTime,
} from "../utils/scheduledOrders";



const customTableStyles = {
  table: { style: { width: "100%", minWidth: "100%" } },
  tableWrapper: { style: { width: "100%" } },
  headCells: {
    style: {
      paddingLeft: "14px",
      paddingRight: "14px",
      fontSize: "14px",
      fontWeight: 700,
      color: "#344054",
      whiteSpace: "normal" as const,
    },
  },
  cells: {
    style: {
      paddingLeft: "14px",
      paddingRight: "14px",
      paddingTop: "8px",
      paddingBottom: "8px",
      fontSize: "12px",
      color: "#344054",
      minWidth: "0",
      overflowWrap: "anywhere" as const,
    },
  },
  rows: {
    style: {
      minHeight: "58px",
      borderBottom: "1px solid #edf0f4",
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid #edf0f4",
      paddingTop: "10px",
      fontSize: "12px",
    },
  },
};

const ScheduleOrdersTable: React.FC<ScheduleOrdersTableProps> = ({
  orders,
  loading,
  onRowClick,
  onUploadClick,
}) => {
  const columns = useMemo<TableColumn<ManagerScheduledOrder>[]>(
    () => [
      {
        name: "Order ID",
        selector: (row) => row.orderId,
        sortable: true,
        width: "14%",
        wrap: true,
      },
      {
        name: "Type",
        selector: (row) => row.type,
        sortable: true,
        width: "14%",
        wrap: true,
      },
      {
        name: "Clinic / Phone",
        selector: (row) => row.clinic,
        sortable: true,
        width: "25%",
        wrap: true,
        cell: (row) => (
          <div className="d-flex flex-column py-2" style={{ lineHeight: "1.4" }}>
            <span
              className="fw-semibold"
              style={{ fontSize: "14px", color: "#26364b", overflowWrap: "anywhere" }}
            >
              {row.clinic || "--"}
            </span>
            <span className="text-muted small mt-1">
              <i className="bi bi-telephone me-1" />
              {row.clinicMobileNo || "--"}
            </span>
          </div>
        ),
      },
      {
        name: "Schedule Date & Time",
        selector: (row) => `${row.scheduleDate} ${row.scheduleTime}`,
        sortable: true,
        width: "25%",
        wrap: true,
        cell: (row) => (
          <div style={{ lineHeight: "1.4", width: "100%" }}>
            <div>{formatScheduleDate(row.scheduleDate)}</div>
            <div className="text-muted small mt-1">
              <i className="bi bi-clock me-1" />
              {formatScheduleTime(row.scheduleTime)}
              {row.scheduleEndTime
                ? ` – ${formatScheduleTime(row.scheduleEndTime)}`
                : ""}
            </div>
          </div>
        ),
      },
      {
        name: "Status",
        width: "22%",
        cell: (row) =>
          row.status?.toLowerCase() === "upload scheduled file" ? (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={(event) => {
                event.stopPropagation();
                onUploadClick(row);
              }}
            >
              Upload Scheduled File
            </button>
          ) : (
            <span
              style={{
                ...getStatusStyle(row.status),
                padding: "5px 10px",
                borderRadius: "5px",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {row.status}
            </span>
          ),
      },
    ],
    [onUploadClick]
  );

  return (
    <div className="manager-orders-card shadow-container rounded mt-3">
      <div className="manager-table-container">
        <DataTable
          columns={columns}
          data={orders}
          progressPending={loading}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          highlightOnHover
          responsive
          selectableRows={false}
          pointerOnHover
          onRowClicked={onRowClick}
          customStyles={customTableStyles}
          noDataComponent={
            <div className="manager-no-orders">
              <i className="bi bi-inbox" />
              <h6>No schedule orders found</h6>
              <p className="mb-0">
                Try changing your search or filter criteria.
              </p>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default ScheduleOrdersTable;
